import { apiClient } from '@/shared/lib/axios';
import { retryClinicUserSsoLink } from '@/services/admin/clinicUsers';
import { ClinicDetail } from '@/types/admin-clinic-detail';
import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

type Response<T> = {
  data: T;
};

export const getClinicDetail = async (
  id: string,
  siteType: string | null,
  options?: { showOutsidePeriod?: boolean }
): Promise<Response<ClinicDetail>> => {
  const response = await apiClient.get<Response<ClinicDetail>>(`/admin/${siteType}/clinics/${id}`, {
    params: {
      show_outside_period: options?.showOutsidePeriod ? 1 : 0,
    },
  });
  return response.data;
};

export type SiteAdminClinicDetailQueryOptions = Pick<
  UseQueryOptions<Response<ClinicDetail>>,
  'staleTime' | 'refetchOnMount' | 'gcTime'
> & {
  showOutsidePeriod?: boolean;
};

export const useSiteAdminClinicDetail = (
  id: string,
  queryOptions?: SiteAdminClinicDetailQueryOptions
) => {
  const { site } = useParams<{ site: string }>();
  const currentSite = site ?? 'medix';
  const { showOutsidePeriod, ...restOptions } = queryOptions ?? {};

  return useQuery({
    queryKey: ['clinic-detail', id, currentSite, !!showOutsidePeriod],
    queryFn: () => getClinicDetail(id, currentSite, { showOutsidePeriod: !!showOutsidePeriod }),
    enabled: !!currentSite && !!id,
    placeholderData: undefined,
    ...restOptions,
  });
};

/**
 * 「SSO連携」 retry button on 治療院詳細's 治療院ユーザー table (BD "SSO Clinic admin" mục 3.0).
 *
 * `id` here is the CLINIC id, not the 管理者 user's — `onSuccess` needs it to invalidate every
 * `['clinic-detail', clinicId, ...]` query key regardless of `siteType`/`showOutsidePeriod`, which
 * only a prefix match can do.
 */
export const useClinicUserSsoLinkRetry = (siteType: string | null, clinicId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (clinicUserId: number) => retryClinicUserSsoLink(siteType!, clinicUserId),
    // `suppressServerErrorModal` KHÔNG phải để cho gọn — cùng lý do useAdminUserSsoLinkRetry nêu.
    //
    // Endpoint này trả 502 khi Cognito/開示 hỏng. Không tắt thì `handleServerError` sẽ bắn
    // SERVER_ERROR_EVENT và nuốt mất toast riêng của màn hình, chỉ còn modal chung 「サーバーエラー」
    // — không bao giờ thấy 「SSO連携に失敗しました。」 hay lý do cụ thể hơn (P1 xong, P3 hỏng).
    meta: { suppressNotFoundToast: true, suppressServerErrorModal: true },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['clinic-detail', clinicId] });
    },
  });
};
